import type { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from "axios";

const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL || "http://localhost:4000";

/**
 * The gateway sets the `accessToken` cookie to live as long as the token it
 * carries (from the JWT's `exp` claim, 12h today — see
 * `gateway/src/utils/cookie-options.ts`). Refresh proactively well before that
 * so the cookie never expires while the page is open — even when the user
 * doesn't interact at all.
 */
export const TOKEN_REFRESH_INTERVAL_MS = 10 * 60 * 1000; // 10 min

/**
 * Store the refresh request while it is running.
 * This prevents multiple refresh requests when several 401 errors happen
 * at the same time from GraphQL or REST calls.
 */
let refreshInFlight: Promise<boolean> | null = null;
let proactiveTimer: ReturnType<typeof setInterval> | null = null;

/**
 * Guests don't have a session or refresh token, so the gateway returns 403
 * on every public page load.
 */
export const hasStoredSession = (): boolean => {
  try {
    return Boolean(localStorage.getItem("userData"));
  } catch {
    return false;
  }
};

/**
 * Refresh the access token by calling the `/refresh-token` endpoint.
 * It gets a new access token using the refresh token cookie.
 * Returns true if it works, otherwise returns false.
 */
export const refreshAccessToken = async (): Promise<boolean> => {
  if (!hasStoredSession()) return false;
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const response = await fetch(`${GATEWAY_URL}/refresh-token`, {
        method: "GET",
        credentials: "include",
      });

      if (response.ok) return true;

      // 401/403 means the refresh token is missing or expired.
      // This is normal, so we don't need to log it as an error.
      if (response.status !== 401 && response.status !== 403) {
        console.error("Token refresh failed:", response.status);
      }
      return false;
    } catch (error) {
      console.error("Token refresh error:", error);
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
};

/**
 * Start periodically refreshing the access token so it never expires while the
 * page is open. Call once on app load after a successful initial refresh.
 * Returns a stop function (also exposed as {@link stopProactiveTokenRefresh})
 * so tests can clean up.
 */
export const startProactiveTokenRefresh = (): (() => void) => {
  if (proactiveTimer) return stopProactiveTokenRefresh;

  proactiveTimer = setInterval(() => {
    void refreshAccessToken();
  }, TOKEN_REFRESH_INTERVAL_MS);

  return stopProactiveTokenRefresh;
};

export const stopProactiveTokenRefresh = (): void => {
  if (proactiveTimer) {
    clearInterval(proactiveTimer);
    proactiveTimer = null;
  }
};

type RetriableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

/**
 * Add an interceptor to axios that checks if a request returns 401.
 * If it does, refresh the token and try the request again.
 * This helps keep API calls working when the token expires,
 * just like Apollo does for GraphQL requests.
 */
export const attachAuthInterceptor = (instance: AxiosInstance): void => {
  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetriableRequest | undefined;

      if (
        error.response?.status !== 401 ||
        !originalRequest ||
        originalRequest._retry
      ) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      const ok = await refreshAccessToken();
      if (!ok) return Promise.reject(error);

      return instance(originalRequest);
    },
  );
};
